'use client';

import { useMemo, useState } from 'react';

import { useGetTasksQuery } from '@/features/tasks/api/tasksApi';
import {
  getTaskStatusLabel,
  getTaskStatusTone,
} from '@/features/tasks/utils/taskStatusDisplay';
import { formatDueDateLabel, getTimelineStatusLabel } from '@/utils/tasks';
import {
  Badge,
  Meter,
  PlusIcon,
  SmartInput,
  ToggleSwitch,
  Tooltip,
} from '@web/ui';
import { formatHours } from '@web/utils';

import styles from './TaskTimeEntrySelect.module.scss';

import type { Task } from '@/features/tasks/types';

type TaskTimeEntrySelectProps = {
  selectedTask: Task | null;
  assigneeId?: string;
  onTaskSelect: (task: Task) => void;
};

function getPrimaryAssignee(task: Task) {
  return task.assignees?.[0] ?? null;
}

function getContextLabel(task: Task) {
  if (task.job?.name) {
    return `${task.job.name}${task.deliverable?.name ? ` • ${task.deliverable.name}` : ''}`;
  }

  return task.client?.company?.name ?? 'Internal';
}

export function TaskTimeEntrySelect({
  selectedTask,
  assigneeId,
  onTaskSelect,
}: TaskTimeEntrySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [includeCompletedTasks, setIncludeCompletedTasks] = useState(false);

  const { data, isLoading, isFetching } = useGetTasksQuery(
    {
      page: 1,
      page_size: 20,
      task_type: 'all',
      for_time_logging: true,
      query: search || undefined,
      exclude_status: includeCompletedTasks ? undefined : 'completed',
      assignee_id: assigneeId || undefined,
      is_descending: true,
      sort_field: 'created_at',
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const tasks = useMemo(() => data?.data?.results ?? [], [data]);

  return (
    <div className={styles.taskTimeEntrySelect}>
      <button
        type="button"
        className={styles.taskTimeEntrySelect__trigger}
        aria-label="Choose task"
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className={styles.taskTimeEntrySelect__selectedTitle}>
          {selectedTask?.title ? (
            <Tooltip title={selectedTask.title} placement="bottom">
              <span className={styles.taskTimeEntrySelect__selectedTitleText}>
                {selectedTask.title}
              </span>
            </Tooltip>
          ) : (
            <span className={styles.taskTimeEntrySelect__emptyTrigger}>
              <PlusIcon
                styles={{
                  color: 'var(--nn-color-interactive)',
                }}
              />
              &nbsp;Task
            </span>
          )}
        </span>
        {selectedTask ? (
          <span className={styles.taskTimeEntrySelect__selectedMeta}>
            {getContextLabel(selectedTask)}
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <div className={styles.taskTimeEntrySelect__panel}>
          <div className={styles.taskTimeEntrySelect__controls}>
            <SmartInput
              type="text"
              name="time-entry-task-search"
              value={search}
              label="Search tasks..."
              onChange={setSearch}
            />
            <ToggleSwitch
              name="time-entry-show-completed-tasks"
              label="Show completed"
              checked={includeCompletedTasks}
              onChange={setIncludeCompletedTasks}
            />
          </div>

          <div className={styles.taskTimeEntrySelect__list}>
            {tasks.length === 0 && !isLoading && !isFetching ? (
              <div className={styles.taskTimeEntrySelect__empty}>
                No tasks found.
              </div>
            ) : null}

            {isLoading || isFetching ? (
              <div className={styles.taskTimeEntrySelect__empty}>
                Loading tasks...
              </div>
            ) : null}

            {tasks.map((task) => {
              const assignee = getPrimaryAssignee(task);

              return (
                <button
                  key={task.id}
                  type="button"
                  className={styles.taskTimeEntrySelect__option}
                  onClick={() => {
                    onTaskSelect(task);
                    setIsOpen(false);
                  }}
                >
                  <span>
                    <span className={styles.taskTimeEntrySelect__taskName}>
                      <span className={styles.taskTimeEntrySelect__title}>
                        {task.title || 'Untitled task'}
                      </span>
                      <Badge
                        label={getTaskStatusLabel(task.status)}
                        tone={getTaskStatusTone(task.status)}
                        size="sm"
                      />
                    </span>
                    <span className={styles.taskTimeEntrySelect__meta}>
                      {getContextLabel(task)}
                    </span>
                  </span>

                  <span className={styles.taskTimeEntrySelect__meters}>
                    <Meter
                      percentage={assignee?.actual_time_tracked_percentage ?? 0}
                      label={`LOE ${formatHours(assignee?.tracked_hours)} / ${formatHours(assignee?.level_of_effort)}h`}
                      ariaLabel="Task LOE meter"
                    />
                    <Meter
                      percentage={task.due_date_time_percentage ?? 0}
                      label={`${formatDueDateLabel(task.due_date)} - ${getTimelineStatusLabel(task.due_date, task.days_to_due)}`}
                      ariaLabel="Task timeline meter"
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
